import { useState, useRef } from 'react';
import { Upload, FileText, X, CheckCircle, Plane, Building, Car, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import * as pdfjs from 'pdfjs-dist';
import { API_BASE_URL } from '../config/environment.js';
import { authFetch } from '../utils/authFetch';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

// Helper function to parse flight date and time
const parseFlightDateTime = (dateStr, timeStr) => {
  try {
    // Parse the date (format: "2024-11-18")
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Parse the time (format: "12:26 PM" or "10:36 AM")
    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!timeMatch) return new Date().getTime();
    
    let [, hours, minutes, period] = timeMatch;
    hours = parseInt(hours);
    minutes = parseInt(minutes);
    
    // Convert to 24-hour format
    if (period.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }
    
    // Create date object
    const date = new Date(year, month - 1, day, hours, minutes);
    return date.getTime();
  } catch (error) {
    console.error('Error parsing flight date/time:', error);
    return new Date().getTime();
  }
};

// Helper function to parse hotel dates without timezone issues
const parseHotelDate = (dateStr) => {
  try {
    if (!dateStr) return new Date().getTime();
    
    // If it's already a timestamp, return it
    if (typeof dateStr === 'number') return dateStr;
    
    // Handle various date formats that might come from PDF parsing
    let date;
    
    // Try parsing as ISO date string (YYYY-MM-DD)
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      date = new Date(year, month - 1, day, 12, 0, 0); // Use noon to avoid timezone issues
    }
    // Try parsing as MM/DD/YYYY or MM-DD-YYYY
    else if (dateStr.match(/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/)) {
      const parts = dateStr.split(/[/-]/);
      const month = parseInt(parts[0]) - 1;
      const day = parseInt(parts[1]);
      const year = parseInt(parts[2]);
      date = new Date(year, month, day, 12, 0, 0); // Use noon to avoid timezone issues
    }
    // Try parsing as a regular date string but force local timezone
    else {
      date = new Date(dateStr + 'T12:00:00'); // Add noon time to avoid timezone shift
    }
    
    return date.getTime();
  } catch (error) {
    console.error('Error parsing hotel date:', error, 'Input:', dateStr);
    return new Date().getTime();
  }
};

// Generic merge-and-deduplicate: appends newItems to existing, skipping duplicates, then sorts
const mergeAndDeduplicate = (existing, newItems, isDuplicate, sortFn) => {
  const merged = [...existing];
  newItems.forEach(item => {
    if (!existing.some(e => isDuplicate(e, item))) {
      merged.push(item);
    }
  });
  return merged.sort(sortFn);
};

// Normalize a parsed flight into the canonical trip-flight shape
// ── Helpers for normalizeFlight ────────────────────────────────────────────────

const fb = (value, fallback) => value || fallback;

const resolveFlightTime = (dateStr, timeStr, fallback) =>
  dateStr && timeStr ? parseFlightDateTime(dateStr, timeStr) : fallback;

const normalizeFlight = (flight) => ({
  flightId: 'flight_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
  airline: fb(flight.airline, 'Unknown'),
  flightNumber: fb(flight.flightNumber, 'TBD'),
  departureAirport: fb(flight.departureAirport, 'TBD'),
  departureTerminal: `${fb(flight.departureAirport, 'TBD')} Airport`,
  departureGate: fb(flight.departureGate, 'TBD'),
  departureTime: resolveFlightTime(flight.departureDate, flight.departureTime, Date.now()),
  arrivalAirport: fb(flight.arrivalAirport, 'TBD'),
  arrivalTerminal: `${fb(flight.arrivalAirport, 'TBD')} Airport`,
  arrivalGate: fb(flight.arrivalGate, 'TBD'),
  arrivalTime: resolveFlightTime(flight.arrivalDate, flight.arrivalTime, Date.now() + 2 * 60 * 60 * 1000),
  seatAssignment: fb(flight.seatAssignment, 'TBD'),
  status: 'Confirmed',
  confirmationCode: fb(flight.confirmationCode, 'PDF-' + Date.now()),
  frequentFlyerNumber: fb(flight.frequentFlyerNumber, 'TBD'),
  aircraft: 'Unknown',
  distance: 'Unknown',
  duration: fb(flight.duration, 'TBD'),
  cabin: 'Unknown'
});

const isFlightDuplicate = (existing, newFlight) =>
  existing.airline === newFlight.airline &&
  existing.flightNumber === newFlight.flightNumber &&
  Math.abs(existing.departureTime - newFlight.departureTime) < 2 * 60 * 60 * 1000;

// Normalize a parsed hotel into the canonical trip-hotel shape
const normalizeHotel = (hotel) => ({
  hotelId: 'hotel_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
  name: hotel.name || 'Hotel from PDF',
  address: hotel.address || 'Address from PDF',
  phoneNumber: '',
  checkInDate: hotel.checkIn ? parseHotelDate(hotel.checkIn) : new Date().getTime(),
  checkOutDate: hotel.checkOut ? parseHotelDate(hotel.checkOut) : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).getTime(),
  confirmationCode: hotel.confirmationNumber || 'PDF-' + Date.now(),
  roomType: 'Standard',
  rate: 0,
  totalRate: 0,
  currency: 'USD',
  frequentGuestNumber: '',
  specialInstructions: '',
  cancellationPolicy: ''
});

const isHotelDuplicate = (existing, newHotel) =>
  existing.name === newHotel.name &&
  existing.address === newHotel.address &&
  Math.abs(existing.checkInDate - newHotel.checkInDate) < 24 * 60 * 60 * 1000;

// Normalize a parsed car rental into the canonical trip-carRental shape
const normalizeCarRental = (rental) => ({
  rentalId: 'rental_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
  company: rental.company || 'Car Rental from PDF',
  pickupLocation: rental.pickupLocation || 'Pickup from PDF',
  returnLocation: rental.returnLocation || 'Return from PDF',
  pickupDate: rental.pickupDate ? parseHotelDate(rental.pickupDate) : new Date().getTime(),
  returnDate: rental.returnDate ? parseHotelDate(rental.returnDate) : new Date().getTime() + 24 * 60 * 60 * 1000,
  pickupTime: rental.pickupTime || '',
  returnTime: rental.returnTime || '',
  confirmationCode: rental.confirmationNumber || 'PDF-' + Date.now(),
  status: 'Confirmed',
  frequentGuestNumber: rental.frequentGuestNumber || '',
  rateCode: '',
  dailyRate: 0,
  totalRate: 0,
  currency: 'USD',
  corporateDiscount: '',
  carType: rental.carType || 'Standard'
});

const isCarRentalDuplicate = (existing, newRental) =>
  existing.company === newRental.company &&
  existing.pickupLocation === newRental.pickupLocation &&
  Math.abs(existing.pickupDate - newRental.pickupDate) < 24 * 60 * 60 * 1000;

// ── Sub-component: data preview section ───────────────────────────────────────
const DataPreviewSection = ({ icon: Icon, iconColor, label, items, renderItem }) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px'
      }}>
        <Icon size={16} style={{ color: iconColor }} />
        <span style={{ fontWeight: '500', color: '#374151' }}>
          {items.length} {label}
        </span>
      </div>
      {items.map((item, index) => (
        <div key={index} style={{
          fontSize: '14px',
          color: '#6b7280',
          marginLeft: '24px',
          marginBottom: '4px'
        }}>
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
};

const DataPreview = ({ parsedData }) => {
  const hasFlights = parsedData.flights && parsedData.flights.length > 0;
  const hasHotels = parsedData.hotels && parsedData.hotels.length > 0;
  const hasCarRentals = parsedData.carRentals && parsedData.carRentals.length > 0;
  const hasNoData = !hasFlights && !hasHotels && !hasCarRentals;

  return (
    <div style={{
      background: '#f8fafc',
      borderRadius: '8px',
      padding: '20px',
      marginTop: '20px'
    }}>
      <h4 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#374151',
        margin: '0 0 16px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <FileText size={16} />
        Data to be added:
      </h4>

      <DataPreviewSection
        icon={Plane}
        iconColor="#dc2626"
        label="Flight(s)"
        items={parsedData.flights}
        renderItem={(flight) => `${flight.airline} ${flight.flightNumber}: ${flight.departureAirport} → ${flight.arrivalAirport}`}
      />
      <DataPreviewSection
        icon={Building}
        iconColor="#1e40af"
        label="Hotel(s)"
        items={parsedData.hotels}
        renderItem={(hotel) => `${hotel.name} - ${hotel.address}`}
      />
      <DataPreviewSection
        icon={Car}
        iconColor="#059669"
        label="Car Rental(s)"
        items={parsedData.carRentals}
        renderItem={(rental) => `${rental.company} - ${rental.carType}`}
      />

      {hasNoData && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#f59e0b'
        }}>
          <AlertCircle size={16} />
          <span style={{ fontSize: '14px' }}>
            No travel data found in PDF
          </span>
        </div>
      )}
    </div>
  );
};

// ── Merge logic (extracted outside component to reduce complexity) ─────────

const MERGE_CONFIGS = [
  { key: 'flights', normalize: normalizeFlight, isDuplicate: isFlightDuplicate, sort: (a, b) => a.departureTime - b.departureTime },
  { key: 'hotels', normalize: normalizeHotel, isDuplicate: isHotelDuplicate, sort: (a, b) => a.checkInDate - b.checkInDate },
  { key: 'carRentals', normalize: normalizeCarRental, isDuplicate: isCarRentalDuplicate, sort: (a, b) => a.pickupDate - b.pickupDate },
];

function mergeTripData(existingTrip, newData) {
  const updatedTrip = { ...existingTrip };

  for (const { key, normalize, isDuplicate, sort } of MERGE_CONFIGS) {
    if (newData[key] && newData[key].length > 0) {
      const normalized = newData[key].map(normalize);
      updatedTrip[key] = mergeAndDeduplicate(existingTrip[key] || [], normalized, isDuplicate, sort);
    }
  }

  if (updatedTrip.flights && updatedTrip.flights.length > 0) {
    const allTimes = updatedTrip.flights.flatMap(f => [f.departureTime, f.arrivalTime]);
    updatedTrip.startDate = Math.min(...allTimes);
    updatedTrip.endDate = Math.max(...allTimes);
  }

  updatedTrip.lastUpdated = Date.now();
  return updatedTrip;
}

async function parseWithBedrock(text, filename) {
  const response = await authFetch(`${API_BASE_URL}/parse-bedrock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pdfText: text, filename })
  });

  if (!response.ok) {
    throw new Error(`Nova Lite API error: ${response.status}`);
  }

  const result = await response.json();
  if (result.success && result.data) {
    return result.data;
  }
  throw new Error(result.error || 'Failed to parse PDF data');
}

// ── Upload area content (extracted to reduce component complexity) ─────────

const UploadAreaContent = ({ isParsing, uploadedFile }) => {
  if (isParsing) {
    return (
      <div>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #dc2626',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }} />
        <p style={{ color: '#6b7280', fontSize: '16px', margin: '0' }}>
          Parsing PDF...
        </p>
      </div>
    );
  }
  if (uploadedFile) {
    return (
      <div>
        <CheckCircle size={48} style={{ color: '#10b981', marginBottom: '16px' }} />
        <p style={{ color: '#374151', fontSize: '16px', fontWeight: '500', margin: '0 0 8px 0' }}>
          {uploadedFile.name}
        </p>
        <p style={{ color: '#6b7280', fontSize: '14px', margin: '0' }}>
          Click to select a different file
        </p>
      </div>
    );
  }
  return (
    <div>
      <Upload size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
      <p style={{ color: '#374151', fontSize: '16px', fontWeight: '500', margin: '0 0 8px 0' }}>
        Drop PDF here or click to browse
      </p>
      <p style={{ color: '#6b7280', fontSize: '14px', margin: '0' }}>
        Upload travel confirmation PDF to add new details
      </p>
    </div>
  );
};

// ── Action buttons (extracted to reduce component complexity) ──────────────

const UpdateActions = ({ parsedData, isUpdating, onUpdate, onClose }) => {
  if (!parsedData) return null;
  return (
    <div style={{
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '24px'
    }}>
      <button
        onClick={onClose}
        style={{
          padding: '12px 20px',
          background: '#f3f4f6',
          color: '#374151',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseOver={(e) => e.target.style.background = '#e5e7eb'}
        onMouseOut={(e) => e.target.style.background = '#f3f4f6'}
      >
        Cancel
      </button>
      <button
        onClick={onUpdate}
        disabled={isUpdating}
        style={{
          padding: '12px 20px',
          background: isUpdating ? '#9ca3af' : 'linear-gradient(135deg, #dc2626 0%, #1e40af 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: isUpdating ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
      >
        {isUpdating ? 'Updating...' : 'Update Trip'}
      </button>
    </div>
  );
};

// ── PDF extraction (outside component) ────────────────────────────────────────

async function extractTextFromPDF(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target.result;
        const pdfDoc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';
        for (let i = 1; i <= pdfDoc.numPages; i++) {
          const page = await pdfDoc.getPage(i);
          const textContent = await page.getTextContent();
          fullText += textContent.items.map(item => item.str).join(' ') + '\n';
        }
        resolve(fullText);
      } catch (error) {
        reject(error);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

async function parsePDFFile(file) {
  const fullText = await extractTextFromPDF(file);
  return await parseWithBedrock(fullText, file.name);
}

// ── Trip update API call (outside component) ──────────────────────────────────

async function saveMergedTrip(trip, parsedData) {
  const updatedTrip = mergeTripData(trip, parsedData);
  const response = await authFetch(`${API_BASE_URL}/trips/${trip.tripId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updatedTrip)
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
  const result = await response.json();
  return result.trip;
}

const TripPDFUpdater = ({ trip, onTripUpdated, onClose }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef(null);

  const processFile = async (file) => {
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setParsedData(null);
      setIsParsing(true);
      try {
        const data = await parsePDFFile(file);
        setParsedData(data);
        toast.success('PDF parsed successfully! Review the changes below.');
      } catch (error) {
        console.error('PDF parsing error:', error);
        toast.error('Failed to parse PDF: ' + error.message);
      } finally {
        setIsParsing(false);
      }
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  const handleFileSelect = async (event) => {
    await processFile(event.target.files[0]);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    await processFile(event.dataTransfer.files[0]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const updateTripWithData = async () => {
    if (!parsedData) return;
    setIsUpdating(true);
    try {
      const savedTrip = await saveMergedTrip(trip, parsedData);
      toast.success('Trip updated successfully!');
      onTripUpdated(savedTrip);
      onClose();
    } catch (error) {
      console.error('Trip update error:', error);
      toast.error('Failed to update trip: ' + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        position: 'relative'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#f3f4f6';
            e.target.style.color = '#374151';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = '#6b7280';
          }}
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #1e40af 100%)',
            borderRadius: '12px',
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Upload size={24} style={{ color: 'white' }} />
          </div>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 4px 0'
            }}>
              Update Trip with PDF
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              margin: '0'
            }}>
              Add new travel details to "{trip.tripName}"
            </p>
          </div>
        </div>

        {/* Upload Area */}
        <label
          htmlFor="trip-pdf-update-input"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          style={{
            border: '2px dashed #d1d5db',
            borderRadius: '12px',
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f9fafb',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '20px',
            display: 'block'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = '#dc2626';
            e.currentTarget.style.backgroundColor = '#fef2f2';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
        >
          <input
            id="trip-pdf-update-input"
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <UploadAreaContent isParsing={isParsing} uploadedFile={uploadedFile} />
        </label>

        {parsedData && <DataPreview parsedData={parsedData} />}

        <UpdateActions parsedData={parsedData} isUpdating={isUpdating} onUpdate={updateTripWithData} onClose={onClose} />
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TripPDFUpdater;
